import { authFetch, buildAgentUrl, throwApiError, unwrapArray, unwrapObject } from './core'
import type { ClassroomStatus } from './models'
import type { BackendChat } from './chats'
import { normalizeChatPayload } from './chats'
import type { StudentItem } from './members'
import { requestOptions } from './workspace'

export interface Classroom {
  id: string
  workspaceId?: string | null
  title: string
  classCode: string
  description?: string | null
  icon?: string | null
  status: ClassroomStatus
  subject?: string | null
  primaryTeacherId?: string | null
  teacherName?: string | null
  isJoined?: boolean
  topics: string[]
  systemPrompt?: string | null
  starterPrompts?: StarterPrompt[] | null
  materials?: ClassroomMaterial[] | null
  createdBy: string
  chatCount: number
  teacherCount: number
  studentCount: number
  usageCredits?: number
  createdAt: string
  updatedAt: string
}

export interface StarterPrompt {
  label: string
  prompt: string
}

export interface ClassroomMaterial {
  id: string
  title: string
  content?: string | null
  kind?: string | null
  attachment?: ClassroomMaterialAttachment | null
  createdAt?: string | null
  updatedAt?: string | null
}

export interface ClassroomMaterialAttachment {
  storageId: string
  filename: string
  mimeType: string
  storageName: string
  sizeBytes?: number | null
}

export interface CreateClassroomPayload {
  title: string
  description?: string
  icon?: string
  status?: ClassroomStatus
  subject?: string
  topics?: string[]
  starterPrompts?: StarterPrompt[] | null
  materials?: ClassroomMaterial[] | null
}

export interface UpdateClassroomPayload {
  title?: string
  description?: string
  icon?: string
  status?: ClassroomStatus
  subject?: string
  topics?: string[]
  starterPrompts?: StarterPrompt[] | null
  materials?: ClassroomMaterial[] | null
  primaryTeacherId?: string | null
  teacherId?: string | null
}

export interface UpdateClassroomStatusPayload {
  status: ClassroomStatus
}

export interface JoinClassroomByCodePayload {
  classCode: string
}

export interface JoinClassroomByCodeResult {
  classroom: Classroom
  joined: boolean
  alreadyEnrolled: boolean
}

function normalizeClassroomPayload(json: unknown): Classroom {
  return unwrapObject<Classroom>(json, ['classroom', 'data', 'item'])
}

export async function fetchClasses(token: string): Promise<Classroom[]> {
  const response = await authFetch(buildAgentUrl('/classes'), requestOptions(token))
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch classes')
  }

  const json = await response.json()
  return unwrapArray<Classroom>(json, ['classes', 'data'])
}

export async function fetchClassroom(token: string, classId: string): Promise<Classroom> {
  const response = await authFetch(buildAgentUrl(`/classes/${classId}`), requestOptions(token))
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch class detail')
  }
  return normalizeClassroomPayload(await response.json())
}

export async function createClassroom(token: string, data: CreateClassroomPayload): Promise<Classroom> {
  const response = await authFetch(buildAgentUrl('/classes'), {
    method: 'POST',
    ...requestOptions(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to create class')
  }
  return normalizeClassroomPayload(await response.json())
}

export async function updateClassroom(
  token: string,
  classId: string,
  data: UpdateClassroomPayload,
): Promise<Classroom> {
  const response = await authFetch(buildAgentUrl(`/classes/${classId}`), {
    method: 'PUT',
    ...requestOptions(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to update class')
  }
  return normalizeClassroomPayload(await response.json())
}

export async function updateClassroomStatus(
  token: string,
  classId: string,
  data: UpdateClassroomStatusPayload,
): Promise<Classroom> {
  const response = await authFetch(buildAgentUrl(`/classes/${classId}/status`), {
    method: 'PATCH',
    ...requestOptions(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to update class status')
  }
  return normalizeClassroomPayload(await response.json())
}

export async function deleteClassroom(token: string, classId: string): Promise<void> {
  const response = await authFetch(buildAgentUrl(`/classes/${classId}`), {
    method: 'DELETE',
    ...requestOptions(token),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to delete class')
  }
}

export async function assignClassTeachers(
  token: string,
  classId: string,
  userIds: string[],
  role: 'PRIMARY' | 'ASSISTANT' = 'PRIMARY',
): Promise<void> {
  const response = await authFetch(buildAgentUrl(`/classes/${classId}/teachers`), {
    method: 'POST',
    ...requestOptions(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ userIds, role }),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to assign class teacher')
  }
}

export async function fetchClassStudents(token: string, classId: string): Promise<StudentItem[]> {
  const response = await authFetch(buildAgentUrl(`/classes/${classId}/students`), requestOptions(token))
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch class students')
  }

  const json = await response.json()
  return unwrapArray<StudentItem>(json, ['students', 'data'])
}

export async function fetchClassChats(token: string, classId: string): Promise<BackendChat[]> {
  const response = await authFetch(buildAgentUrl(`/classes/${classId}/chats`), requestOptions(token))
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch class conversations')
  }

  const json = await response.json()
  return unwrapArray<unknown>(json, ['chats', 'data']).map((item) => ({
    ...normalizeChatPayload(item),
    classId,
  }))
}

export async function joinClassByCode(
  token: string,
  data: JoinClassroomByCodePayload,
): Promise<JoinClassroomByCodeResult> {
  const response = await authFetch(buildAgentUrl("/classes/join-by-code"), {
    method: 'POST',
    ...requestOptions(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to join class')
  }

  return response.json() as Promise<JoinClassroomByCodeResult>
}

export async function joinKnownClassByCode(
  token: string,
  classId: string,
  data: JoinClassroomByCodePayload,
): Promise<JoinClassroomByCodeResult> {
  const response = await authFetch(buildAgentUrl(`/classes/${classId}/join`), {
    method: 'POST',
    ...requestOptions(token, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    await throwApiError(response, 'Failed to join class')
  }

  return response.json() as Promise<JoinClassroomByCodeResult>
}
